import { TrackerStatus } from "@redotech/order-tracking-common/order-tracking-tracker-status-definition";
import {
  OrderTrackingOrderFulfillmentStatus,
  OrderTrackingShipmentStatus,
} from "@redotech/order-tracking-common/order-tracking-types";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import { Provider } from "@redotech/redo-order-common/provider";

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

// Anchor preview dates to today (UTC start-of-day) so the date hero stays
// in the future and reads as realistic, but is stable within a session and
// doesn't shift mid-render.
function todayUtcStart(): Date {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
}

export function getPreviewShipment(): TrackingPageShipment {
  const today = todayUtcStart().getTime();
  return {
    id: "preview-shipment",
    status: OrderTrackingShipmentStatus.IN_TRANSIT,
    trackingCode: "9361289987654321098765",
    trackingUrl: null,
    carrier: "USPS",
    // ETA = ~3 days out so the date hero always lands in the future.
    estimatedDeliveryAt: new Date(today + 3 * DAY_MS),
    deliveredAt: null,
    updatedAt: new Date(today - 4 * HOUR_MS),
    trackingEvents: [
      {
        timestamp: new Date(today - 4 * HOUR_MS),
        status: TrackerStatus.IN_TRANSIT,
        message: "Arrived at facility",
        location: { city: "Seattle", state: "WA", country: "US" },
      },
      {
        timestamp: new Date(today - DAY_MS - 2 * HOUR_MS),
        status: TrackerStatus.IN_TRANSIT,
        message: "Departed facility",
        location: { city: "Lehi", state: "UT", country: "US" },
      },
      {
        timestamp: new Date(today - 2 * DAY_MS - 3 * HOUR_MS),
        status: TrackerStatus.PRE_TRANSIT,
        message: "Shipping label created",
        location: { city: "Lehi", state: "UT", country: "US" },
      },
    ],
    // Custom HTML `{{ shipments[0].lineItems[0].* }}` previews read this.
    lineItems: [
      {
        id: "preview-line-item",
        name: "Sample Product",
        variantTitle: "Medium / Blue",
        quantity: 1,
        imageUrl: null,
        productId: "preview-product",
        variantId: "preview-variant",
      },
    ],
  };
}

function getSecondPreviewShipment(): TrackingPageShipment {
  const today = todayUtcStart().getTime();
  return {
    id: "preview-shipment-2",
    status: OrderTrackingShipmentStatus.PRE_TRANSIT,
    trackingCode: "1Z999AA10123456789",
    trackingUrl: null,
    carrier: "UPS",
    estimatedDeliveryAt: new Date(today + 5 * DAY_MS),
    deliveredAt: null,
    updatedAt: new Date(today - HOUR_MS),
    trackingEvents: [
      {
        timestamp: new Date(today - HOUR_MS),
        status: TrackerStatus.PRE_TRANSIT,
        message: "Shipping label created",
        location: { city: "Lehi", state: "UT", country: "US" },
      },
    ],
    lineItems: [],
  };
}

export function getPreviewOrder(): TrackingPageOrder {
  return {
    externalOrderId: "preview-order",
    internalMongoId: "000000000000000000000001",
    teamId: "000000000000000000000002",
    orderNumber: "1001",
    customer: { email: "preview@example.com", name: "Preview Customer" },
    sourceName: "web",
    cancelReason: null,
    cancelledAt: null,
    fulfillmentStatus: OrderTrackingOrderFulfillmentStatus.FULFILLED,
    currency: "USD",
    source: Provider.SHOPIFY,
    shippingAddress: {
      streetAddress: "123 Main St",
      city: "Seattle",
      state: "WA",
      country: "United States",
      countryCode: "US",
      postalCode: "98101",
    },
    shippingMethod: "Standard",
    shipments: [getPreviewShipment(), getSecondPreviewShipment()],
    lineItems: [],
  };
}
